import type { NameBadgeText } from './name-badge-text.interface';

const LABEL_MARGIN_PX = 4;
const TEXT_LINE_HEIGHT_RATIO = 1.12;
const MAX_POSITION_FONT_PX = 18;
const MIN_POSITION_FONT_PX = 10;
const MAX_NAME_FONT_PX = 36;
const MIN_NAME_FONT_PX = 14;

/** Longest known position — used to tune minimum font sizes. */
export const LONGEST_EMPLOYEE_POSITION =
  'Начальник отдела организации перевозок';

interface TextBlockLayout {
  fontSize: number;
  lines: string[];
  blockHeight: number;
}

function layoutSingleLineText(
  ctx: CanvasRenderingContext2D,
  text: string,
  layoutWidth: number,
  options: {
    maxFontPx: number;
    minFontPx: number;
    fontSizeDivisor: number;
    minFontFloor: number;
  },
): TextBlockLayout {
  const maxTextWidth = layoutWidth - LABEL_MARGIN_PX * 2;
  let fontSize = Math.min(
    options.maxFontPx,
    Math.max(
      options.minFontFloor,
      Math.floor(layoutWidth / options.fontSizeDivisor),
    ),
  );

  while (fontSize >= options.minFontPx) {
    ctx.font = `700 ${fontSize}px sans-serif`;
    if (ctx.measureText(text).width <= maxTextWidth) {
      break;
    }
    fontSize -= 1;
  }

  ctx.font = `700 ${fontSize}px sans-serif`;
  const lineHeight = fontSize * TEXT_LINE_HEIGHT_RATIO;

  return {
    fontSize,
    lines: [text],
    blockHeight: lineHeight,
  };
}

function layoutPosition(
  ctx: CanvasRenderingContext2D,
  positionText: string,
  layoutWidth: number,
): TextBlockLayout {
  return layoutSingleLineText(ctx, positionText, layoutWidth, {
    maxFontPx: MAX_POSITION_FONT_PX,
    minFontPx: MIN_POSITION_FONT_PX,
    fontSizeDivisor: 5,
    minFontFloor: 11,
  });
}

function layoutName(
  ctx: CanvasRenderingContext2D,
  nameText: string,
  layoutWidth: number,
): TextBlockLayout {
  return layoutSingleLineText(ctx, nameText, layoutWidth, {
    maxFontPx: MAX_NAME_FONT_PX,
    minFontPx: MIN_NAME_FONT_PX,
    fontSizeDivisor: 3.5,
    minFontFloor: 18,
  });
}

function drawTextBlock(
  ctx: CanvasRenderingContext2D,
  layout: TextBlockLayout,
  startY: number,
): number {
  const { fontSize, lines } = layout;
  const lineHeight = fontSize * TEXT_LINE_HEIGHT_RATIO;
  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.font = `700 ${fontSize}px sans-serif`;

  let cursorY = startY;
  for (const line of lines) {
    ctx.fillText(line, canvasCenterX(ctx), cursorY);
    cursorY += lineHeight;
  }
  return cursorY;
}

function canvasCenterX(ctx: CanvasRenderingContext2D): number {
  return ctx.canvas.width / 2;
}

/**
 * Renders a two-line chest badge without rotation.
 * Uses full print-head width so long positions stay on one line.
 * Layout (top → bottom): position (smaller), name (larger).
 */
export function renderNameBadgeCanvas(
  canvas: HTMLCanvasElement,
  badge: NameBadgeText,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('CANVAS_CONTEXT');
  }

  const positionText = badge.position?.trim() ?? '';
  const nameText = badge.name.trim();
  if (!nameText) {
    throw new Error('NAME_BADGE_EMPTY');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const layoutWidth = canvas.width;
  const layoutHeight = canvas.height;

  const positionLayout = positionText
    ? layoutPosition(ctx, positionText, layoutWidth)
    : null;
  const nameLayout = layoutName(ctx, nameText, layoutWidth);

  const positionBlockHeight = positionLayout?.blockHeight ?? 0;
  const gapCount = positionText ? 2 : 1;
  const totalContentHeight =
    positionBlockHeight + nameLayout.blockHeight + LABEL_MARGIN_PX * gapCount;

  let cursorY = Math.max(
    LABEL_MARGIN_PX,
    (layoutHeight - totalContentHeight) / 2,
  );

  if (positionLayout) {
    cursorY = drawTextBlock(ctx, positionLayout, cursorY);
    cursorY += LABEL_MARGIN_PX;
  }

  drawTextBlock(ctx, nameLayout, cursorY);
}
